import { useMemo, useState, useEffect } from 'react';
import {
  X,
  Loader2,
  RefreshCw,
  PanelLeftClose,
  PanelLeftOpen,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Input } from './ui/input';

interface UscSection {
  section_number: string;
  section_title?: string;
}

interface UscPart {
  part_number: string;
  part_title: string;
  sections: UscSection[];
}

interface UscViewerProps {
  section: string;
  title?: string;
  content?: any;
  loading: boolean;
  error?: string;
  onClose: () => void;
  onReload: () => void;
  onSectionSelect: (section: string) => void;
  structure?: UscPart[];
  structureLoading?: boolean;
}

const normalizeSectionInput = (input: string) => input.replace(/[^0-9]/g, '').trim();

const convertUscContentToHtml = (content: any): string => {
  if (!content) return '';
  if (typeof content === 'string') return content;

  let html = '';

  // Add header if available
  if (content.header) {
    html += `<div class="text-lg font-semibold mb-4">${content.header}</div>`;
  }

  // Add main content
  if (content.content) {
    html += content.content;
  } else if (content.raw_html) {
    html += content.raw_html;
  } else if (content.text) {
    // Split by paragraphs and wrap in HTML
    const paragraphs = content.text.split('\n\n').filter((p: string) => p.trim());
    html += paragraphs.map((p: string) => `<p>${p.trim()}</p>`).join('');
  }

  return html || (typeof content === 'object' ? `<pre>${JSON.stringify(content, null, 2)}</pre>` : '');
};

export function UscViewer({
  section,
  title,
  content,
  loading,
  error,
  onClose,
  onReload,
  onSectionSelect,
  structure,
  structureLoading,
}: UscViewerProps) {
  const [sectionInput, setSectionInput] = useState('');
  const [browserCollapsed, setBrowserCollapsed] = useState(false);
  const [expandedParts, setExpandedParts] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!section || !structure) return;
    const normalized = normalizeSectionInput(section);

    structure.forEach((part) => {
      const hasDirect = part.sections.some(
        (sec) => normalizeSectionInput(sec.section_number) === normalized
      );

      if (hasDirect) {
        setExpandedParts((prev) => ({ ...prev, [part.part_number]: true }));
      }
    });
  }, [section, structure]);

  const htmlContent = useMemo(() => convertUscContentToHtml(content), [content]);

  const handleSectionSubmit = () => {
    const normalized = normalizeSectionInput(sectionInput);
    if (!normalized) return;
    onSectionSelect(normalized);
    setSectionInput('');
  };

  const handleSectionInputKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSectionSubmit();
    }
  };

  const handlePartToggle = (partNumber: string) => {
    setExpandedParts((prev) => ({ ...prev, [partNumber]: !prev[partNumber] }));
  };

  const handleSectionClick = (sectionNumber: string) => {
    onSectionSelect(sectionNumber);
  };

  return (
    <div className="flex flex-col h-full min-h-0 bg-white border-l border-gray-200">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide">35 U.S.C. Section</p>
          <h2 className="text-lg font-semibold text-gray-900">
            {title || `35 U.S.C. § ${section}`}
          </h2>
          <p className="text-sm text-gray-600">United States Code</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => setBrowserCollapsed((prev) => !prev)}
          >
            {browserCollapsed ? (
              <>
                <PanelLeftOpen className="w-4 h-4" />
                Show Navigator
              </>
            ) : (
              <>
                <PanelLeftClose className="w-4 h-4" />
                Hide Navigator
              </>
            )}
          </Button>
          <Button variant="outline" size="sm" className="gap-2" onClick={onReload} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Reload
          </Button>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {!browserCollapsed && (
          <div className="w-80 border-r border-gray-200 flex flex-col min-h-0">
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">
                  Jump to Section
                </label>
                <div className="flex gap-2">
                  <Input
                    placeholder="e.g., 101"
                    value={sectionInput}
                    onChange={(e) => setSectionInput(e.target.value)}
                    onKeyDown={handleSectionInputKey}
                  />
                  <Button onClick={handleSectionSubmit}>Open</Button>
                </div>
                <p className="text-[11px] text-gray-500 mt-1">Enter section (e.g., 101, 102, 103)</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                  Parts & Sections
                </p>
                {structureLoading ? (
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    Loading structure...
                  </div>
                ) : !Array.isArray(structure) || structure.length === 0 ? (
                  <p className="text-xs text-gray-500">Structure unavailable.</p>
                ) : null}
              </div>
            </div>

            <ScrollArea className="flex-1 min-h-0 px-3 pb-4">
              <div className="space-y-1">
                {Array.isArray(structure) && structure.map((part) => {
                  const partExpanded = expandedParts[part.part_number];
                  return (
                    <div key={part.part_number} className="border border-gray-100 rounded-lg">
                      <button
                        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-50"
                        onClick={() => handlePartToggle(part.part_number)}
                      >
                        <div>
                          <p className="text-[11px] uppercase text-gray-500">Part {part.part_number}</p>
                          <p className="text-sm text-gray-900">{part.part_title}</p>
                        </div>
                        {partExpanded ? (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        )}
                      </button>

                      {partExpanded && part.sections.length > 0 && (
                        <div className="border-t border-gray-100">
                          <div className="py-2 space-y-1">
                            {part.sections.map((uscSection) => {
                              const normalizedSection = normalizeSectionInput(uscSection.section_number);
                              const isActive = normalizeSectionInput(section) === normalizedSection;
                              return (
                                <button
                                  key={normalizedSection}
                                  className={`w-full text-left px-4 py-1.5 text-xs ${
                                    isActive
                                      ? 'bg-blue-50 text-blue-700'
                                      : 'text-gray-700 hover:bg-gray-50'
                                  }`}
                                  onClick={() => handleSectionClick(normalizedSection)}
                                >
                                  § {normalizedSection}
                                  {uscSection.section_title && (
                                    <span className="block text-[11px] text-gray-500">
                                      {uscSection.section_title}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
        )}

        <div className="flex-1 flex flex-col min-h-0">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-gray-500 flex flex-col items-center gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                <p className="text-sm">Loading 35 U.S.C. § {section}...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center px-6 text-center">
              <div>
                <p className="text-sm text-red-600 font-medium mb-2">Failed to load section</p>
                <p className="text-sm text-gray-600 mb-4">{error}</p>
                <Button onClick={onReload}>Try Again</Button>
              </div>
            </div>
          ) : htmlContent ? (
            <ScrollArea className="flex-1 min-h-0">
              <div
                className="prose prose-sm max-w-none px-6 py-6 text-gray-800"
                dangerouslySetInnerHTML={{ __html: htmlContent }}
              />
            </ScrollArea>
          ) : (
            <div className="flex-1 flex items-center justify-center px-6 text-center">
              <p className="text-sm text-gray-600">
                No content available for this section. Try reloading or selecting a different section.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
